import { useState } from "react";
import {
  Phone,
  MapPin,
  Clock,
  Instagram,
  Mail,
  Send,
  CheckCircle,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WA_SVG = (
  <svg viewBox="0 0 24 24" className="fill-current">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

const subjects = [
  "Planejamento Personalizado",
  "Pacotes de Férias",
  "Viagens de Luxo",
  "Viagens em Grupo",
  "Consultoria de Viagem",
  "Lua de Mel",
  "Outro",
];

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Por favor, preencha os campos obrigatórios.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      toast.success(res.data.message || "Mensagem enviada com sucesso!");
      setSent(true);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error("Erro ao enviar. Tente pelo WhatsApp abaixo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-16 md:pt-20">
      {/* Page Hero */}
      <section className="page-hero flex items-center justify-center min-h-[300px] relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{
            backgroundImage:
              "url(https://images.pexels.com/photos/4907631/pexels-photo-4907631.jpeg)",
          }}
        />
        <div className="text-center text-white px-4">
          <p className="text-[#89A6A3] font-semibold tracking-widest text-sm uppercase mb-3">
            Fale conosco
          </p>
          <h1 className="font-heading text-4xl md:text-6xl font-bold">
            Contato
          </h1>
          <p className="mt-4 text-white/80 text-lg max-w-xl mx-auto">
            Estamos prontos para transformar seu sonho de viagem em realidade.
          </p>
        </div>
      </section>

      <section
        data-testid="contact-section"
        className="py-20 md:py-28 bg-white"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <p className="text-[#89A6A3] font-semibold tracking-widest text-sm uppercase mb-3">
                Informações
              </p>
              <h2 className="font-heading text-4xl font-bold text-[#2E4E72] mb-6">
                Vamos planejar sua próxima aventura?
              </h2>
              <p className="text-slate-600 leading-relaxed mb-8">
                Entre em contato com nossa equipe. Estamos disponíveis para
                tirar dúvidas, montar roteiros e criar a viagem perfeita para
                você.
              </p>

              {/* WhatsApp Highlight Card */}
              <div
                data-testid="whatsapp-contact-card"
                className="bg-green-50 border-2 border-green-200 rounded-2xl p-6 mb-6"
              >
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-green-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <span className="text-white w-7 h-7">{WA_SVG}</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-[#2E4E72]">
                      Prefere o WhatsApp?
                    </h3>
                    <p className="text-slate-600 text-sm mt-1">
                      Respondemos em minutos!
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 mt-4">
                  {/* <a href="https://wa.me/5513996995877?text=Olá!%20Gostaria%20de%20planejar%20uma%20viagem."
                    target="_blank" rel="noopener noreferrer" data-testid="whatsapp-chat-btn"
                    className="bg-green-500 text-white px-5 py-2.5 rounded-full font-semibold text-sm hover:bg-green-600 transition-colors flex items-center gap-2">
                    <span className="w-4 h-4">{WA_SVG}</span> Iniciar Conversa
                  </a> */}
                  <a
                    href="https://wa.me/5513996995877?text=Olá!%20Gostaria%20de%20um%20orçamento%20personalizado."
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="whatsapp-quote-btn"
                    className="border-2 border-green-500 text-green-700 px-5 py-2.5 rounded-full font-semibold text-sm hover:bg-green-50 transition-colors"
                  >
                    Solicitar Orçamento
                  </a>
                </div>
              </div>

              <div className="space-y-4">
                <a
                  href="tel:+5513996995877"
                  data-testid="contact-phone-link"
                  className="flex items-center gap-4 p-4 rounded-xl bg-[#F4F7F6] hover:bg-[#B0CAC5]/30 transition-colors group"
                >
                  <div className="w-12 h-12 bg-[#2E4E72] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#89A6A3] transition-colors">
                    <Phone size={20} className="text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-[#2E4E72]">(13) 99699-5877</p>
                    <p className="text-slate-500 text-sm">
                      Ligue ou envie WhatsApp
                    </p>
                  </div>
                </a>
                <a
                  href="mailto:contato@mahloviagens.com.br"
                  data-testid="contact-email-link"
                  className="flex items-center gap-4 p-4 rounded-xl bg-[#F4F7F6] hover:bg-[#B0CAC5]/30 transition-colors group"
                >
                  <div className="w-12 h-12 bg-[#2E4E72] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#89A6A3] transition-colors">
                    <Mail size={20} className="text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-[#2E4E72]">
                      contato@mahloviagens.com.br
                    </p>
                    <p className="text-slate-500 text-sm">Envie um e-mail</p>
                  </div>
                </a>
                <div className="flex items-center gap-4 p-4 rounded-xl bg-[#F4F7F6]">
                  <div className="w-12 h-12 bg-[#2E4E72] rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin size={20} className="text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-[#2E4E72]">
                      R. Othon Feliciano, 02 - Cj. 43
                    </p>
                    <p className="text-slate-500 text-sm">
                      Vila Matias, Santos - SP
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 rounded-xl bg-[#F4F7F6]">
                  <div className="w-12 h-12 bg-[#2E4E72] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock size={20} className="text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-[#2E4E72]">
                      Segunda a Sexta até 18:00
                    </p>
                    <p className="font-bold text-[#2E4E72]">Sábado até 12:00</p>
                    {/* <p className="text-slate-500 text-sm">Segunda a Sábado</p> */}
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <p className="text-[#2E4E72] font-bold mb-4 text-sm tracking-widest uppercase">
                  Redes Sociais
                </p>
                <div className="flex gap-4">
                  <a
                    href="https://www.instagram.com/mahloviagens/"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-instagram"
                    className="flex items-center gap-2 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:opacity-90 transition-opacity"
                  >
                    <Instagram size={16} /> @mahloviagens
                  </a>
                  <a
                    href="https://wa.me/5513996995877"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-whatsapp-btn"
                    className="flex items-center gap-2 bg-green-500 text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-green-600 transition-colors"
                  >
                    <span className="w-4 h-4">{WA_SVG}</span> WhatsApp
                  </a>
                </div>
              </div>
            </div>

            {/* Form */}
            <div
              data-testid="contact-form-container"
              className="bg-[#F4F7F6] p-8 rounded-2xl"
            >
              {sent ? (
                <div
                  data-testid="contact-success-msg"
                  className="flex flex-col items-center justify-center h-full text-center py-12"
                >
                  <CheckCircle size={64} className="text-[#89A6A3] mb-6" />
                  <h3 className="font-heading text-2xl font-bold text-[#2E4E72] mb-3">
                    Mensagem Enviada!
                  </h3>
                  <p className="text-slate-600 mb-6">
                    Recebemos sua mensagem e entraremos em contato em breve.
                  </p>
                  <button
                    data-testid="send-another-btn"
                    onClick={() => setSent(false)}
                    className="bg-[#89A6A3] text-[#2E4E72] font-bold px-6 py-3 rounded-full hover:bg-[#6B8C89] hover:text-white transition-colors"
                  >
                    Enviar nova mensagem
                  </button>
                </div>
              ) : (
                <>
                  <h3 className="font-heading text-2xl font-bold text-[#2E4E72] mb-6">
                    Envie uma Mensagem
                  </h3>
                  <form
                    data-testid="contact-form"
                    onSubmit={handleSubmit}
                    className="space-y-5"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label
                          htmlFor="name"
                          className="block text-sm font-semibold text-[#2E4E72] mb-1.5"
                        >
                          Nome completo <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={form.name}
                          onChange={handleChange}
                          data-testid="input-name"
                          placeholder="Seu nome"
                          className="w-full h-11 px-4 bg-white border border-slate-200 rounded-lg text-sm text-[#2E4E72] placeholder:text-slate-400 transition-all"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="email"
                          className="block text-sm font-semibold text-[#2E4E72] mb-1.5"
                        >
                          E-mail <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={form.email}
                          onChange={handleChange}
                          data-testid="input-email"
                          placeholder="seu@email.com"
                          className="w-full h-11 px-4 bg-white border border-slate-200 rounded-lg text-sm text-[#2E4E72] placeholder:text-slate-400 transition-all"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label
                          htmlFor="phone"
                          className="block text-sm font-semibold text-[#2E4E72] mb-1.5"
                        >
                          Telefone / WhatsApp
                        </label>
                        <input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={form.phone}
                          onChange={handleChange}
                          data-testid="input-phone"
                          placeholder="(11) 99999-9999"
                          className="w-full h-11 px-4 bg-white border border-slate-200 rounded-lg text-sm text-[#2E4E72] placeholder:text-slate-400 transition-all"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="subject"
                          className="block text-sm font-semibold text-[#2E4E72] mb-1.5"
                        >
                          Assunto
                        </label>
                        <select
                          id="subject"
                          name="subject"
                          value={form.subject}
                          onChange={handleChange}
                          data-testid="select-subject"
                          className="w-full h-11 px-4 bg-white border border-slate-200 rounded-lg text-sm text-[#2E4E72] transition-all"
                        >
                          <option value="">Selecione um assunto</option>
                          {subjects.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label
                        htmlFor="message"
                        className="block text-sm font-semibold text-[#2E4E72] mb-1.5"
                      >
                        Mensagem <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows={5}
                        value={form.message}
                        onChange={handleChange}
                        data-testid="input-message"
                        placeholder="Conta-nos sobre a viagem dos seus sonhos: destino, datas, número de pessoas..."
                        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-sm text-[#2E4E72] placeholder:text-slate-400 transition-all resize-none"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="contact-submit-btn"
                      className="w-full bg-[#89A6A3] text-[#2E4E72] font-bold py-4 rounded-full text-lg hover:bg-[#6B8C89] hover:text-white disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300 hover:scale-[1.02] flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <>
                          <div className="w-5 h-5 border-2 border-[#2E4E72]/30 border-t-[#2E4E72] rounded-full animate-spin" />
                          Enviando...
                        </>
                      ) : (
                        <>
                          <Send size={18} /> Enviar Mensagem
                        </>
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section data-testid="map-section" className="h-72 md:h-96">
        <iframe
          title="Localização Mahlo Viagens"
          src="https://maps.google.com/maps?q=R.+Othon+Feliciano+02+Santos+SP+Brazil&t=&z=16&ie=UTF8&iwloc=&output=embed"
          className="w-full h-full border-0"
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </section>
    </div>
  );
};

export default Contact;
